import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Dashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  if (!token) {
    navigate('/login');
  }

  // States for tasks and task input
  const [tasks, setTasks] = useState([]);
  const [taskInput, setTaskInput] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('Medium');

  // States for editing an existing task
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editInput, setEditInput] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editPriority, setEditPriority] = useState('Medium');

  const config = { headers: { 'x-auth-token': token } };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTasks = async () => {
    try {
      const { data } = await axios.get('http://localhost:5000/api/tasks', config);
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch tasks.');
    }
  };

  const addTask = async () => {
    if (!taskInput.trim()) return;
    try {
      const newTask = { text: taskInput, dueDate: dueDate || null, priority };
      const { data } = await axios.post('http://localhost:5000/api/tasks', newTask, config);
      setTasks([...tasks, data]);
      setTaskInput('');
      setDueDate('');
      setPriority('Medium');
      toast.success('Task added successfully!');
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task.');
    }
  };

  const toggleTask = async (id, completed) => {
    try {
      const { data } = await axios.put(`http://localhost:5000/api/tasks/${id}`, { completed: !completed }, config);
      setTasks(tasks.map(task => task._id === id ? data : task));
      toast.success('Task updated!');
    } catch (error) {
      console.error('Error toggling task:', error);
      toast.error('Failed to update task.');
    }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`http://localhost:5000/api/tasks/${id}`, config);
      setTasks(tasks.filter(task => task._id !== id));
      toast.success('Task deleted!');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task.');
    }
  };

  // Editing functions
  const startEditing = (task) => {
    setEditingTaskId(task._id);
    setEditInput(task.text);
    setEditDueDate(task.dueDate ? task.dueDate.substring(0, 10) : '');
    setEditPriority(task.priority || 'Medium');
  };

  const saveEdit = async (id) => {
    try {
      const updatedTask = { text: editInput, dueDate: editDueDate || null, priority: editPriority };
      const { data } = await axios.put(`http://localhost:5000/api/tasks/${id}`, updatedTask, config);
      setTasks(tasks.map(task => task._id === id ? data : task));
      cancelEdit();
      toast.success('Task updated successfully!');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task.');
    }
  };

  const cancelEdit = () => {
    setEditingTaskId(null);
    setEditInput('');
    setEditDueDate('');
    setEditPriority('Medium');
  };

  // Returns styling classes based on priority
  const getPriorityClasses = (priority) => {
    if (priority === 'High') return 'border-red-500';
    if (priority === 'Medium') return 'border-yellow-500';
    if (priority === 'Low') return 'border-green-500';
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8 relative overflow-hidden">
      {/* Floating Gradient Blobs for Aesthetic Effect */}
      <div className="absolute top-[-100px] left-[-100px] w-96 h-96 bg-blue-500 opacity-25 blur-3xl rounded-full"></div>
      <div className="absolute bottom-[-100px] right-[-100px] w-96 h-96 bg-purple-500 opacity-25 blur-3xl rounded-full"></div>
      
      <ToastContainer />
      <div className="max-w-4xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text text-center mb-10"
        >
          Your Task Dashboard
        </motion.h2>

        {/* Task Input Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="bg-gray-900/50 backdrop-blur-lg p-10 rounded-xl shadow-lg mb-10 hover:bg-gray-800/70 hover:shadow-2xl hover:scale-105 transform transition-all duration-300"
        >
          <h3 className="text-2xl font-semibold mb-4">Add a New Task</h3>
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              value={taskInput}
              onChange={(e) => setTaskInput(e.target.value)}
              placeholder="Enter task description..."
              className="flex-1 p-3 rounded bg-gray-600 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 hover:bg-gray-500 transition-colors"
            />
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="p-3 rounded bg-gray-600 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 hover:bg-gray-500 transition-colors"
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="p-3 rounded bg-gray-600 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 hover:bg-gray-500 transition-colors"
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
            <button
              onClick={addTask}
              className="bg-blue-600 hover:bg-blue-700 transition py-3 px-6 rounded"
            >
              Add Task
            </button>
          </div>
        </motion.div>

        {/* Task Cards */}
        <AnimatePresence>
          <ul className="space-y-6">
            {tasks.map(task => (
              <motion.li
                key={task._id}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                layout
                className={`bg-gray-800 p-6 rounded-xl shadow-xl border-4 ${getPriorityClasses(task.priority)} transform transition duration-300 hover:scale-105 hover:rotate-1`}
              >
                {editingTaskId === task._id ? (
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={editInput}
                      onChange={(e) => setEditInput(e.target.value)}
                      className="w-full p-3 rounded bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <div className="flex flex-col md:flex-row gap-4">
                      <input
                        type="date"
                        value={editDueDate}
                        onChange={(e) => setEditDueDate(e.target.value)}
                        className="flex-1 p-3 rounded bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      <select
                        value={editPriority}
                        onChange={(e) => setEditPriority(e.target.value)}
                        className="p-3 rounded bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => saveEdit(task._id)}
                        className="bg-green-500 hover:bg-green-600 transition py-2 px-4 rounded"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="bg-red-500 hover:bg-red-600 transition py-2 px-4 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row items-center justify-between">
                    <div
                      onClick={() => toggleTask(task._id, task.completed)}
                      className="cursor-pointer"
                    >
                      <h3 className={`text-2xl font-semibold ${task.completed ? 'line-through text-gray-400' : 'text-white'}`}>
                        {task.text}
                      </h3>
                      {task.dueDate && (
                        <p className="text-lg text-gray-300">
                          Due: {new Date(task.dueDate).toLocaleDateString()}
                        </p>
                      )}
                      <p className="text-lg text-gray-300">Priority: {task.priority}</p>
                    </div>
                    <div className="flex gap-4 mt-4 md:mt-0">
                      <button
                        onClick={() => startEditing(task)}
                        className="bg-yellow-500 hover:bg-yellow-600 transition py-2 px-4 rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteTask(task._id)}
                        className="bg-red-500 hover:bg-red-600 transition py-2 px-4 rounded"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </motion.li>
            ))}
          </ul>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default Dashboard;
